import { Link } from 'react-router-dom';
import './AboutPage.css';

const metrics = [
  { value: '45+', label: 'bots orchestrated weekly' },
  { value: '12 ms', label: 'average action latency' },
  { value: '24/7', label: 'production monitoring' },
];

const principles = [
  {
    title: 'Production parity',
    description:
      'Every simulation mirrors live hardware, so the logic you approve in Origami is the logic that ships to the floor.',
  },
  {
    title: 'Visible collaboration',
    description:
      'Designers, technicians, and operators can co-edit behavior trees with shared annotations and role-aware reviews.',
  },
  {
    title: 'Shipping velocity',
    description:
      'Reusable templates, instant XML exports, and CI-ready integrations make repeatable deployments the default.',
  },
];

const cultureHighlights = [
  {
    title: 'Design for operators',
    description:
      'We build UI affordances that speak the language of technicians—clear state cues, smart diffs, and contextual logging.',
  },
  {
    title: 'Rigor in the loop',
    description:
      'A release checklist couples automated testing with human sign-off, ensuring traceability from prototype to production.',
  },
  {
    title: 'Learning by doing',
    description:
      'We pair with robotics teams in the field, folding real-world feedback into every iteration of the platform.',
  },
];

const teamMembers = [
  {
    name: 'Kavin Prakash',
    title: 'Founder & CEO',
    bio: 'Systems engineer turned founder who believes robotics teams deserve the same creative tooling as software teams.',
    imageAlt: 'Portrait of Kavin Prakash',
    imageSrc: '/kavin_photo.png',
  },
  {
    name: 'Ben Mamut',
    title: 'Embedded Software Engineer',
    bio: 'Bridges firmware and cloud services, crafting integrations that keep edge devices and behavior trees in lockstep.',
    imageAlt: 'Portrait of Ben Mamut',
    imageSrc: '/ben_photo.png',
  },
  {
    name: 'Alexey Plagov',
    title: 'Software Engineer',
    bio: 'Focuses on editor UX and graph performance so complex autonomy plans stay responsive and easy to reason about.',
    imageAlt: 'Portrait of Alexey Plagov',
    imageSrc: '/alexey_photo.png',
  },
];

function AboutPage() {
  return (
    <div className="about-shell">
      <header className="about-hero">
        <div className="layout-container about-hero__inner">
          <div className="about-hero__content">
            <span className="about-label">About Origami</span>
            <h1>Engineering confident autonomy for every robotics team.</h1>
            <p>
              Origami gives robotics builders a visual, production-ready canvas for behavior trees. We blend live
              telemetry, high-fidelity simulation, and opinionated workflows so teams can script nuanced behaviors and
              deploy them with certainty.
            </p>
            <div className="about-hero__actions">
              <Link to="/" className="about-button about-button--ghost">
                Back to home
              </Link>
              <Link to="/contact" className="about-button about-button--primary">
                Talk with us
              </Link>
            </div>

            <dl className="about-hero__metrics" aria-label="Origami performance highlights">
              {metrics.map((item) => (
                <div key={item.label} className="about-metric">
                  <dt>{item.value}</dt>
                  <dd>{item.label}</dd>
                </div>
              ))}
            </dl>
          </div>

          <aside className="about-hero__panel" aria-label="Origami principles">
            <h2>How we measure success</h2>
            <ul>
              {principles.map((principle) => (
                <li key={principle.title}>
                  <span className="about-panel__label">{principle.title}</span>
                  <p>{principle.description}</p>
                </li>
              ))}
            </ul>
          </aside>
        </div>
      </header>

      <section className="about-story">
        <div className="layout-container">
          <div className="about-story__intro">
            <h2>Purpose-built for teams that run critical automation.</h2>
            <p>
              From warehouse fleets to lab experiments, Origami keeps complex behaviors observable, auditable, and ready
              to ship. We obsess over the handoff between experimentation and production so your crew can iterate
              quickly without sacrificing reliability.
            </p>
          </div>
          <div className="about-culture">
            {cultureHighlights.map((highlight) => (
              <article key={highlight.title} className="about-culture__item">
                <h3>{highlight.title}</h3>
                <p>{highlight.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="about-video" aria-labelledby="about-video-title">
        <div className="layout-container about-video__inner">
          <div className="about-video__content">
            <span className="about-label about-label--soft">Platform walkthrough</span>
            <h2 id="about-video-title">See how Origami choreographs resilient robotics flows.</h2>
            <p>
              Explore our behavior builder, tree analytics, and deployment tooling in under three minutes. The demo
              showcases how operators debug live runs and apply fixes without disrupting production schedules.
            </p>
          </div>
          <div className="about-video__frame" role="presentation">
            <video className="about-video__player" controls preload="metadata" src="/Beaker_video.mp4" />
          </div>
        </div>
      </section>

      <section className="about-team" aria-labelledby="about-team-title">
        <div className="layout-container">
          <div className="about-team__intro">
            <span className="about-label about-label--soft">Team</span>
            <h2 id="about-team-title">Meet the people behind the platform.</h2>
            <p>
              Pragmatic dreamers, hardware tinkerers, and design-minded engineers—bound by the conviction that robotics
              tooling should be as intuitive as modern software stacks.
            </p>
          </div>
          <div className="about-team__grid">
            {teamMembers.map((member) => (
              <article key={member.name} className="about-card">
                {member.imageSrc ? (
                  <div className="about-card__image about-card__image--photo">
                    <img
                      className={`about-card__photo${
                        member.name === 'Alexey Plagov' ? ' about-card__photo--alexey' : ''
                      }`}
                      src={member.imageSrc}
                      alt={member.imageAlt}
                    />
                  </div>
                ) : (
                  <div className="about-card__image about-card__image--placeholder" aria-hidden="true">
                    <span>Image</span>
                  </div>
                )}
                <div className="about-card__content">
                  <h3>{member.name}</h3>
                  <p className="about-card__title">{member.title}</p>
                  <p className="about-card__bio">{member.bio}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default AboutPage;
